import { useEffect, useState } from 'react';

/**
 * 吸顶布局的统一度量（single source of truth）
 * ------------------------------------------------------------------
 * 渐进收起式吸顶：页头（MerchantHeader）不吸顶、随内容滚出视口，
 * 一级 tab 起才是吸顶层。滚动链路：
 *
 *   ① 应用级顶栏   baseLayout 的 `.contentHeader`
 *                  （position: fixed; height: 44px; z-index: 1000）
 *   ② 页面级头     shell/MerchantHeader（商家名那一栏）
 *                  —— 不吸顶，随内容滚出（data-pg-sticky-header 仅作测量标记）
 *   ③ 一级 tab     detail/index.less 的 .topTabs 里的 .ant-tabs-nav
 *                  （sticky，吸在应用顶栏正下方）
 *   ④ 二级导航     ui/SubNav，标记 data-pg-sticky-subnav
 *   ⑤ 表格表头    ui/DataTable 的 antd sticky.offsetHeader
 *
 * 踩过的坑：
 *   · 表格表头不能使用 antd 默认的 offsetHeader 0；
 *   · 这些高度**写死必错**：顶栏与导航的实际高度受内容、antd 默认样式与主题影响。
 *
 * 因此这里统一**实测**（读真实 offsetHeight），把每一层的吸顶位置写进 CSS 变量，
 * less 侧各层的 top 值都写变量名（如 --pg-nav-top）；JS 侧（antd 需要数字）用返回的 contentTop。
 * CSS 与 JS 两侧因此永远用同一个值。
 *
 * 注：baseLayout 的 `.warpContent` 顶部偏移与应用顶栏高度属于历史布局契约，
 * 吸顶坐标仍需把应用顶栏高度计入。
 */

/** 兜底：应用级顶栏高度 */
export const APP_TOP_BAR_FALLBACK = 44;

/** 兜底：页面级吸顶头高度（未挂载时使用） */
export const PAGE_HEADER_FALLBACK = 72;

/** 兜底：一级 tab 导航高度 */
export const PRIMARY_NAV_FALLBACK = 48;

/** 兜底：二级导航高度 */
export const SUB_NAV_FALLBACK = 0;

const px = (v) => `${v}px`;

const readMetrics = () => {
  /* 取「第一个真正可见」的匹配元素：页面上可能有内层 antd Tabs、隐藏的
     Layout 等，未挂载时 offsetHeight 为 0——跳过它们，取不到就用兜底值。 */
  const pick = (selector, fallback) => {
    if (typeof document === 'undefined') return fallback;
    const nodes = document.querySelectorAll(selector);
    for (let i = 0; i < nodes.length; i += 1) {
      const h = nodes[i].offsetHeight;
      if (h) return h;
    }
    return fallback;
  };

  return {
    appTop: pick('.ant-layout-header', APP_TOP_BAR_FALLBACK),
    pageHeaderH: pick('[data-pg-sticky-header]', PAGE_HEADER_FALLBACK),
    /* 一级导航是 DOM 顺序上第一个可见的 .ant-tabs-nav（内层页签的在其后） */
    primaryNavH: pick('.ant-tabs-nav', PRIMARY_NAV_FALLBACK),
    subNavH: pick('[data-pg-sticky-subnav]', SUB_NAV_FALLBACK),
  };
};

/**
 * 读取（并在运行时刷新）吸顶相关高度。
 * @returns { appTop, pageHeaderH, primaryNavH, subNavH,
 *            navTop, subNavTop, contentTop, tableOffset }
 *          —— navTop/subNavTop 供 less 用（同时也写进 CSS 变量）；
 *             contentTop / tableOffset 是内容与表头该吸在哪里的像素值。
 */
export const useStickyMetrics = () => {
  const [metrics, setMetrics] = useState(readMetrics);

  useEffect(() => {
    const sync = () => {
      const next = readMetrics();
      /* 渐进收起：页头随内容滚出，一级 tab 直接吸在应用顶栏下方 */
      const navTop = next.appTop;
      const subNavTop = navTop + next.primaryNavH;
      const contentTop = subNavTop + next.subNavH;

      /* 同步 CSS 变量：less 侧各层的 top 都取这里，避免 CSS / JS 各算一套 */
      if (typeof document !== 'undefined' && document.documentElement) {
        const root = document.documentElement.style;
        root.setProperty('--pg-app-top', px(next.appTop));
        root.setProperty('--pg-nav-top', px(navTop));
        root.setProperty('--pg-subnav-top', px(subNavTop));
        root.setProperty('--pg-content-top', px(contentTop));
      }

      setMetrics({ ...next, navTop, subNavTop, contentTop });
    };

    sync();
    /* 首帧测量时机偏早：页头/导航可能还没挂载（异步取数、懒加载），
       此时会落到兜底值。这里补几次重测，且只在有改动时 setState（下一帧即可修正）。 */
    const retries = [0, 120, 400, 1000].map((ms) => setTimeout(sync, ms));
    window.addEventListener('resize', sync);
    return () => {
      retries.forEach(clearTimeout);
      window.removeEventListener('resize', sync);
    };
  }, []);

  return {
    ...metrics,
    /* 表格表头吸顶位置与内容区一致；保留 tableOffset 别名兼容 DataTable 用法 */
    tableOffset: metrics.contentTop,
  };
};

export default useStickyMetrics;
